import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { faTag as faSolidTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverContent,
  PopperTrigger,
  Text,
} from '@modusign/design-system';

import { WindowModal } from '@app/components/Modal/WindowModal';
import { ThinButton } from '@app/components/ThinButton';
import { LabelChangeResult } from '@app/features/label/components/LabelPopovers/context/types';
import LabelApplyModalPopover from '@app/features/label/components/LabelPopovers/LabelApplyPopover';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import Label from '@app/store/documents/Label';

interface DocumentLabelSelectionContentProps {
  isPopoverOpen: boolean;
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLabels?: Label[];
  setSelectedLabelIds: (labelIds?: string[]) => void;
}

function DocumentLabelSelectionContent({
  isPopoverOpen,
  setIsPopoverOpen,
  selectedLabels,
  setSelectedLabelIds,
}: DocumentLabelSelectionContentProps) {
  const isMobileWidth = useResponsiveValue([true, false, false]);

  const handleLabelSelect = (labelChanges: LabelChangeResult) => {
    setSelectedLabelIds(labelChanges.filter((l) => l.apply).map((l) => l.labelId));
    setIsPopoverOpen(false);
  };

  return (
    <Flex justifyContent="space-between">
      <Flex alignItems="center" color="iconNeutralBase" minHeight="22px">
        <Flex alignItems="center" height="18px" justifyContent="center" mr="2px" width="18px">
          <FontAwesomeIcon color="#848484" icon={selectedLabels ? faSolidTag : faTag} />
        </Flex>
        <Text color="textNeutralLight" sx={{ maxWidth: '500px' }} textAlign="left">
          {(!selectedLabels || selectedLabels.length === 0) && (
            <Text>문서의 라벨을 설정해 주세요.</Text>
          )}
          {selectedLabels && selectedLabels.length > 1 && (
            <Text>{`${selectedLabels[0].name} 외 ${selectedLabels.length - 1}개`}</Text>
          )}
          {selectedLabels && selectedLabels.length === 1 && <Text>{selectedLabels[0].name}</Text>}
        </Text>
      </Flex>
      <Flex alignItems="center" flex="none" minHeight="22px" pl={4} textAlign="right">
        {isMobileWidth ? (
          <>
            <Button
              px={4}
              variant="subtle"
              weight="light"
              width="narrow"
              onClick={() => setIsPopoverOpen(true)}
            >
              라벨 설정
            </Button>
            <WindowModal
              isOpen={isPopoverOpen}
              maxWidth="xxxs"
              onRequestClose={() => setIsPopoverOpen(false)}
            >
              <LabelApplyModalPopover
                initialLabeled={Object.fromEntries(
                  selectedLabels?.map(({ id }) => [id, 'ALL']) ?? []
                )}
                mode="limit"
                onClose={() => setIsPopoverOpen(false)}
                onSubmit={handleLabelSelect}
              />
            </WindowModal>
          </>
        ) : (
          <Popover
            isOpen={isPopoverOpen}
            mode="click"
            nested
            placement="bottom-end"
            onOpenChange={setIsPopoverOpen}
          >
            <PopperTrigger>
              <ThinButton onClick={() => setIsPopoverOpen(!isPopoverOpen)}>라벨 설정</ThinButton>
            </PopperTrigger>
            <PopoverContent>
              <Box
                bg="white"
                border="1px solid"
                borderColor="boxBorderNeutralBase"
                borderRadius="4px"
                sx={{ zIndex: 1000 }}
                width="xxxs"
              >
                <LabelApplyModalPopover
                  initialLabeled={Object.fromEntries(
                    selectedLabels?.map(({ id }) => [id, 'ALL']) ?? []
                  )}
                  mode="limit"
                  onSubmit={handleLabelSelect}
                />
              </Box>
            </PopoverContent>
          </Popover>
        )}
      </Flex>
    </Flex>
  );
}

export default DocumentLabelSelectionContent;
